import { useState, useMemo } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useUserContext } from '../context/UserContext';
import { useAnalytics, useCategories } from '../hooks';
import {
  MetricsCards,
  StatusDistributionChart,
  TrendsChart,
  CategoryChart,
  SupplierTable,
  AnalyticsDateFilter,
  analyticsDateRangeToParams,
  DEFAULT_ANALYTICS_DATE_RANGE,
} from '../components/analytics';

/**
 * Format date range for display
 */
function formatDateRange(startDate: number, endDate: number): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  return `${start.toLocaleDateString('en-US', options)} – ${end.toLocaleDateString('en-US', options)}, ${end.getFullYear()}`;
}

export function AnalyticsDashboard() {
  const { brandId } = useUserContext();
  const [dateRange, setDateRange] = useState(DEFAULT_ANALYTICS_DATE_RANGE);

  // Memoize date options to prevent infinite refetches
  // (analyticsDateRangeToParams uses Date.now() for presets which changes on every render)
  const dateOptions = useMemo(() => analyticsDateRangeToParams(dateRange), [dateRange]);

  const { data: categoriesData } = useCategories(!!brandId, brandId);
  const categoryLabels = useMemo(
    () =>
      (categoriesData ?? []).reduce<Record<string, string>>(
        (acc, c) => {
          acc[c.categoryId] = c.label;
          return acc;
        },
        {}
      ),
    [categoriesData]
  );

  const { data, isLoading, error, refetch, isFetching } = useAnalytics({
    startDate: dateOptions.startDate,
    endDate: dateOptions.endDate,
    trendDays: dateOptions.trendDays,
  });

  const analytics = data?.data;

  if (error) {
    return (
      <div className="p-4 lg:p-6">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-900">Failed to load analytics</h3>
            <p className="text-sm text-red-700 mt-1 mb-3">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total complaints for metrics
  const totalComplaints = analytics
    ? analytics.byCategory.reduce((sum, cat) => sum + cat.count, 0)
    : 0;

  return (
    <div className="px-4 pt-2 pb-24 lg:px-6 lg:pt-3 lg:pb-6 space-y-5">
      {/* Header row with date range and controls */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {analytics ? formatDateRange(analytics.period.startDate, analytics.period.endDate) : (
            <span className="inline-block h-4 w-32 bg-gray-100 rounded animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <AnalyticsDateFilter value={dateRange} onChange={setDateRange} />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <MetricsCards
        metrics={analytics?.metrics ?? {
          avgResolutionTime: null,
          avgAcknowledgmentTime: null,
          acknowledgmentRate: 0,
          resolutionRate: 0,
        }}
        totalComplaints={totalComplaints}
        isLoading={isLoading}
      />

      {/* Charts Row 1: Trends and Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TrendsChart trends={analytics?.trends ?? []} isLoading={isLoading} />
        <StatusDistributionChart
          byStatus={analytics?.bySupplier.reduce(
            (acc, supplier) => {
              acc.submitted += supplier.byStatus.submitted;
              acc.under_review += supplier.byStatus.under_review;
              acc.approved += supplier.byStatus.approved;
              acc.rejected += supplier.byStatus.rejected;
              return acc;
            },
            { submitted: 0, under_review: 0, approved: 0, rejected: 0 }
          ) ?? { submitted: 0, under_review: 0, approved: 0, rejected: 0 }}
          isLoading={isLoading}
        />
      </div>

      {/* Charts Row 2: Category and Suppliers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CategoryChart
          byCategory={analytics?.byCategory ?? []}
          isLoading={isLoading}
          categoryLabels={categoryLabels}
        />
        <SupplierTable bySupplier={analytics?.bySupplier ?? []} isLoading={isLoading} />
      </div>
    </div>
  );
}
